import queryString from 'query-string';

/** How many batches (including the first) load via scroll before pausing. */
export const AUTO_LOAD_BATCH_WINDOW = 3;

/**
 * Highest batch page exposed in crawlable URLs (`?page=N`), rel=next/prev,
 * and load-more hrefs. Users can still load batches beyond this via scroll /
 * "Load more" / "Show more"; the address bar stays at this cap.
 */
export const MAX_URL_PAGE = 10;

/**
 * Clamp a 1-based jobs batch page into the crawlable URL range [1, MAX_URL_PAGE].
 *
 * @param {number} page
 * @returns {number}
 */
export function clampJobsPage(page) {
  const parsed = typeof page === 'number' ? page : parseInt(page, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return 1;
  }
  return Math.min(Math.floor(parsed), MAX_URL_PAGE);
}

/**
 * Parse a 1-based jobs batch page from location.search.
 * Invalid or missing values default to 1. Values above MAX_URL_PAGE clamp down.
 *
 * @param {string} search
 * @returns {number}
 */
export function parseJobsPage(search = '') {
  const { page } = queryString.parse(search);
  const parsed = parseInt(page, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return 1;
  }
  return Math.min(parsed, MAX_URL_PAGE);
}

/**
 * Build a jobs board path + query for a given batch page.
 * Omits `page` when page is 1. Caps at MAX_URL_PAGE.
 *
 * @param {string} pathname
 * @param {string} search - current location.search (filters preserved)
 * @param {number} page
 * @returns {string}
 */
export function buildJobsBatchUrl(pathname, search = '', page = 1) {
  const params = { ...queryString.parse(search) };
  const cappedPage = clampJobsPage(page);

  if (cappedPage <= 1) {
    delete params.page;
  } else {
    params.page = String(cappedPage);
  }

  const qs = queryString.stringify(params);
  return qs ? `${pathname}?${qs}` : pathname;
}

/**
 * Sequentially load briefs for batches 1…throughPage (inclusive, clamped).
 * Awaits each load so Redux append order stays correct for deep links.
 *
 * @param {Function} loadFn - (page, ...loadArgs) => Promise|any (bound loadBriefs or dispatch wrapper)
 * @param {number} throughPage
 * @param {...any} loadArgs - args after page for loadBriefs
 * @returns {Promise<void>}
 */
export async function loadBriefsThroughPage(loadFn, throughPage, ...loadArgs) {
  const capped = clampJobsPage(throughPage);
  for (let p = 1; p <= capped; p += 1) {
    await loadFn(p, ...loadArgs);
  }
}
